import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { router } from '@/router'
import { useConfigStore } from '@/store/config'
import sync from '@/sync/service'
import type { CloseTillDraft, Movement, Till } from '@/types'

export const useTillStore = defineStore(
  'till',
  () => {
    const startedShifts = ref<string[]>([])
    const startedShiftsWithCashAmount = ref<string[]>([])
    const shiftsEnabled = ref(false)
    const selectedCashTill = ref<Till | null>(null)
    const closeTillDrafts = ref<Record<string, CloseTillDraft>>({})

    const lastDay = ref<{ start: string | null; end: string | null }>({
      start: null,
      end: null
    })

    const selectedCashTillIsStarted = computed(() => {
      return selectedCashTill.value && startedShifts.value.includes(selectedCashTill.value.id)
    })

    const tillIsStarted = computed(() => {
      return (tillId: string) => startedShifts.value.includes(tillId)
    })

    const shiftIsStarted = computed(() => startedShifts.value.length > 0)

    const askForForTillStartAmount = computed(() => {
      return (
        shiftsEnabled.value &&
        !(
          selectedCashTill.value &&
          startedShiftsWithCashAmount.value.includes(selectedCashTill.value.id)
        )
      )
    })

    const dayShift = computed(() => {
      const configStore = useConfigStore()
      const workingTimeEnd = configStore.config.workingTime?.end
      const now = new Date()

      const [hours, minutes] = (workingTimeEnd ?? '03:00').split(':').map(Number)
      const maxStart = new Date(now)
      maxStart.setHours(hours, minutes, 0, 0)
      if (maxStart > now) {
        maxStart.setDate(maxStart.getDate() - 1)
      }

      let start: Date
      if (lastDay.value.start) {
        const lastDayStart = new Date(lastDay.value.start)
        start = lastDayStart > maxStart ? lastDayStart : maxStart
      } else {
        start = maxStart
      }

      return {
        start,
        end: lastDay.value.end ? new Date(lastDay.value.end) : null
      }
    })

    function refreshStartedShifts(shifts: string[]) {
      startedShifts.value = shifts
      if (shiftsEnabled.value && !selectedCashTillIsStarted.value) {
        if (router.currentRoute.value.name !== 'employees') {
          router.push({ name: 'employees' })
        }
      }
    }

    function refreshStartedShiftsWithCashAmount(shifts: string[]) {
      startedShiftsWithCashAmount.value = shifts
    }

    function refreshShiftsEnabled(enabled: boolean) {
      shiftsEnabled.value = enabled
      if (shiftsEnabled.value && !selectedCashTillIsStarted.value) {
        if (router.currentRoute.value.name !== 'employees') {
          router.push({ name: 'employees' })
        }
      }
    }

    function refreshLastDay(lastDayData: { start: string | null; end: string | null }) {
      lastDay.value = lastDayData
    }

    function updateSelectedTillIfNull(till: Till) {
      if (!selectedCashTill.value) {
        selectedCashTill.value = till
      }
    }

    function setTill(till: Till) {
      selectedCashTill.value = till
    }

    function startShift({
      amount,
      tillId,
      method
    }: {
      amount: number
      tillId: string
      method: string
    }) {
      sync.record('shiftStarted', {
        amount,
        start: new Date(),
        tillId: tillId,
        method
      })
    }

    function endShift({
      amounts,
      tillId
    }: {
      amounts: { method: string; amount: number }[]
      tillId: string
    }) {
      sync.record('shiftEnded', {
        amounts,
        end: new Date(),
        tillId: tillId || selectedCashTill.value!.id
      })
    }

    function closeDay() {
      return new Promise<void>(resolve => {
        sync.record('dayClosed', {})
        sync.observeEnd(async () => {
          resolve()
        })
      })
    }

    function saveCloseTillDraft(tillId: string, draft: CloseTillDraft) {
      closeTillDrafts.value[tillId] = draft
    }

    function clearCloseTillDraft(tillId: string) {
      delete closeTillDrafts.value[tillId]
    }

    function addMovement({ amount, detail, movementType, tillId, method }: Movement) {
      sync.record('movementAdded', {
        amount,
        detail,
        movementType,
        method,
        tillId: tillId || selectedCashTill.value!.id,
        creationTime: new Date()
      })
    }

    return {
      startedShifts,
      startedShiftsWithCashAmount,
      shiftsEnabled,
      selectedCashTill,
      selectedCashTillIsStarted,
      tillIsStarted,
      shiftIsStarted,
      askForForTillStartAmount,
      lastDay,
      dayShift,
      closeTillDrafts,

      refreshStartedShifts,
      refreshStartedShiftsWithCashAmount,
      refreshShiftsEnabled,
      updateSelectedTillIfNull,
      setTill,
      startShift,
      endShift,
      closeDay,
      addMovement,
      refreshLastDay,
      saveCloseTillDraft,
      clearCloseTillDraft
    }
  },
  {
    persist: true
  }
)

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useTillStore, import.meta.hot))
}
